import React, { Component } from 'react';
import { ItemProvider, isValidType } from '@platform/template';
import { getGlobalStorage } from '@platform/api';
import { dfs, ROW_STATUS, DEFAULT_COLOR } from '../../utils';
import classNames from 'classnames';
import { Tooltip } from '@platform/base';

import Cell from './Cell.js';

export default function useStore(Table) {
    class StoreTable extends Component {
        timer = null;

        constructor(props) {
            super(props);
            this.state = { hoveredKey: '' };
        }

        render() {
            let { rowProps, hasSuspendedRow } = this.props,
                { hoveredKey } = this.state;
            return <Table
                {...this.props}
                rowProps={(row, viewIndex) => {
                    let rawProps = rowProps?.(row, viewIndex) ?? {};

                    if (hasSuspendedRow) {
                        return {
                            ...rawProps,
                            // 优有些悬浮状态需要保留，依赖事件冒泡，所以没用hover做
                            onMouseEnter: (...rest) => {
                                this.timer = setTimeout(() => {
                                    this.setState({ hoveredKey: row.key });
                                    clearTimeout(this.timer);
                                    this.timer = null;
                                }, 300);
                                rawProps.onMouseEnter?.(...rest);
                            },
                            onMouseLeave: (...rest) => {
                                if (this.timer) {
                                    clearTimeout(this.timer);
                                    this.timer = null;
                                } else {
                                    this.setState({ hoveredKey: '' });
                                }
                                rawProps.onMouseLeave?.(...rest);
                            },
                            className: classNames(rawProps.className, { hovered: hoveredKey === row.key }),
                        };
                    }
                    return rawProps;
                }}
            />;
        }
    }
    return ItemProvider(StoreTable);
}

useStore.displayName = 'useStore';
useStore.dependence = [];
useStore.hooks = function () {
    this.store.validatedInfo = new Map();
    this.store.componentConfig = {};
    this._configTranslator = a => a;
    return {
        setConfig: config => {
            this.store.componentConfig = config;
        },
        getConfig: () => {
            return this._configTranslator(this.store.componentConfig) ?? {};
        },
        setPageScope: config => {
            this.store._pageScope = config;
        },
        getPageScope: () => {
            return this.store._pageScope;
        },
        setConfigTranslator: translator => {
            this._configTranslator = translator;
        },
        setGridTableId: id => {
            this.store.gridTableId = id;
        },
        getGridTableId: () => {
            return this.store.gridTableId;
        },
        setStatus: status => {
            this.store.status = status;
            if (status === 'browse') {
                this.setActiveCell({ colKey: '', rowKey: '' });
                this.clearValidate();
            }
            this.forceUpdate();
        },
        getStatus: () => {
            return this.store.status || 'browse';
        },
        setCellValue: (rowKey, colKey, { value, shouldUpdate = true, changeStatus = true }) => {
            if (this.store.rowDataMap[rowKey]) {
                let row = this.getRowData(rowKey);
                if (changeStatus && row.status === ROW_STATUS.ORIGIN) {
                    row.status = ROW_STATUS.EDIT;
                }

                // 取消校验状态
                if (value?.value && this.getCellProps(rowKey, colKey, 'invalid')) {
                    this.setCellProps(rowKey, colKey, { invalid: false });
                    this.clearCellValidate(rowKey, colKey);
                }

                this.store.rowDataMap[rowKey].values[colKey] = this._formatCellValue({ ...row.values, [colKey]: value }, colKey);
                shouldUpdate && this.updateComponent(this.itemIDManager.getID({ rowKey, colKey }));

                if (this.getColumnProps(colKey, 'itemtype') === 'number' && this.getColumnProps(colKey, 'istotal')) {
                    this._updateTotal([colKey]);
                    this.forceUpdate();
                }
            } else {
                console.warn('rowKey输入有误');
            }
        },
        getCellValue: (rowKey, colKey) => {
            return this.store.rowDataMap[rowKey]?.values[colKey] || {};
        },
        setCellProps: (rowKey, colKey, props) => {
            if (this.store.cellPropsMap[rowKey]) {
                let currentProps = this.store.cellPropsMap[rowKey][colKey];
                this.store.cellPropsMap[rowKey][colKey] = { ...currentProps, ...props };
            } else {
                this.store.cellPropsMap[rowKey] = { [colKey]: props };
            }

            this.updateComponent(this.itemIDManager.getID({ rowKey, colKey }));
        },
        getCellProps: (rowKey, colKey, prop) => {
            if (prop) {
                return this.store.cellPropsMap[rowKey]?.[colKey]?.[prop] ?? this.store.columnsMap[colKey]?.[prop];
            }
            // 初始情况时，继承columnsMap
            return { ...this.store.columnsMap[colKey], ...this.store.cellPropsMap[rowKey]?.[colKey] };
        },
        // 对行设置的属性都转为对单元格设置属性
        setRowProps: (rowKey, props) => {
            this.store.leafColumnKeys.forEach(colKey => {
                this.setCellProps(rowKey, colKey, props);
            });
        },
        clearCellProps: () => {
            this.store.cellPropsMap = {};
        },
        // 应该没有使用场景，除了status（status单独抽出来了，在下面）
        getRowProps: () => {

        },
        setRowStatus: (rowKey, status) => {
            let row = this.getRowData(rowKey);
            row.status = status;
            this.setRowData(row);
        },
        getRowStatus: rowKey => {
            return this.getRowData(rowKey).status;
        },
        setActiveCell: ({ colKey, rowKey }) => {
            let prevActiveCell = this.store.activeCell || {};
            this.store.activeCell = { colKey, rowKey };
            if (prevActiveCell.rowKey !== rowKey || prevActiveCell.colKey !== colKey) {
                // this.focusOnCell(rowKey, colKey);

                let itemId = this.itemIDManager.getID({ colKey, rowKey }),
                    prevId = this.itemIDManager.getID(prevActiveCell);

                // blur为了触发前一个Item的编辑后
                prevId && this.blurComponent(prevId);
                prevId && this.updateComponent(prevId);
                itemId && this.updateComponent(itemId);
            }
        },
        getActiveCell: () => {
            return this.store.activeCell || {};
        },
        validateToSave: customerFunc => {
            let requiredErrInfo = new Map(),
                regErrInfo = new Map(),
                maxErrInfo = new Map(),
                customerErrInfo = new Map(),
                errLabelInfo = new Map(),
                errInfo = {},
                rows = this.getRows().filter(row => row.status !== ROW_STATUS.DELETE), //filter row
                lengthTypes = ['input', 'number', 'textarea'],
                columns = this.getVisibleLeafColumnKeys(),
                firstErrorCell = [];

            // 2. 所有可见数据对每一列验证数据
            rows.forEach(row => {
                let rowErrorLabel = new Set();
                let rowRegErrorLabel = new Set();
                let rowRequiredErrorLabel = new Set();
                let rowMaxErrorLabel = new Set();
                // let rowToast = '第'+ (row.viewIndex + 1) + '行：';
                columns.forEach(colKey => {
                    let invalid = false,
                        reg = this.getColumnProps(colKey, 'reg'),
                        maxlength = this.getColumnProps(colKey, 'maxlength'),
                        itemtype = this.getColumnProps(colKey, 'itemtype'),
                        required = this.getColumnProps(colKey, 'required'),
                        value = this.getCellValue(row.key, colKey)?.value;
                    if (customerFunc && typeof customerFunc === 'function') { //自定义校验
                        let validated = customerFunc({
                            required: this.getColumnProps(colKey, 'required'),
                            rowStatus: row.status,
                            attrcode: colKey,
                            values: row.values,
                            column: this.getColumnProps(),
                            rowKeyValue: row.key,
                            saga_status: row.values?.saga_status || {},
                        });
                        invalid = !validated.passed;
                        invalid && rowErrorLabel.add(colKey);
                        if (!firstErrorCell.length) {
                            firstErrorCell = [row.key, colKey];
                        }
                    } else { //平台校验
                        if (reg && !(typeof reg === 'function' ? reg(value) : reg.test(value))) {
                            rowRegErrorLabel.add(colKey);
                            invalid = true;
                            if (!firstErrorCell.length) {
                                firstErrorCell = [row.key, colKey];
                            }
                        }
                        if (maxlength && lengthTypes.includes(itemtype) && value && String(value).length > maxlength) {
                            rowMaxErrorLabel.add(colKey);
                            invalid = true;
                            if (!firstErrorCell.length) {
                                firstErrorCell = [row.key, colKey];
                            }
                        }
                        if (required && [undefined, null, ''].includes(value)) {
                            rowRequiredErrorLabel.add(colKey);
                            invalid = true;
                            if (!firstErrorCell.length) {
                                firstErrorCell = [row.key, colKey];
                            }
                        }
                    }

                    // invalid && rowErrorLabel.add(colKey);
                    this.setCellProps(row.key, colKey, { invalid });
                });
                errLabelInfo.set(customerErrInfo, rowErrorLabel);
                errLabelInfo.set(regErrInfo, rowRegErrorLabel);
                errLabelInfo.set(requiredErrInfo, rowRequiredErrorLabel);
                errLabelInfo.set(maxErrInfo, rowMaxErrorLabel);

                errLabelInfo.forEach((errorLabel, ErrInfo) => {
                    if (errorLabel.size) {
                        ErrInfo.set(row.key, errorLabel);
                    }
                });
            });
            errInfo = {
                regErrInfo,
                maxErrInfo,
                requiredErrInfo,
                customerErrInfo,
                firstErrorCell,
            };
            // 3. 存储已被检查过的数据 TODO 还需要一个get接口
            this.store.validatedInfo = errInfo.requiredErrInfo;

            if (regErrInfo.size || maxErrInfo.size || requiredErrInfo.size) {
                // 为了更新CardTable页签的错误标识
                this.forceUpdate();
            }
            return errInfo;
        },
        validate: customerFunc => {
            let errInfo = new Map(),
                rows = this.getRows().filter(row => row.status !== ROW_STATUS.DELETE), //filter row
                columns = this.getVisibleLeafColumnKeys();

            // 2. 所有可见数据对每一列验证数据
            rows.forEach(row => {
                let rowErrorLabel = new Set();
                // let rowToast = '第'+ (row.viewIndex + 1) + '行：';
                columns.forEach(colKey => {
                    let invalid = false,
                        reg = this.getColumnProps(colKey, 'reg'),
                        value = this.getCellValue(row.key, colKey)?.value,
                        invalidValue = [undefined, null, ''].includes(value),
                        required = this.getCellProps(row.key, colKey, 'required') ?? this.getColumnProps(colKey, 'required');
                    
                    if (!required) {
                        return;
                    }

                    if (customerFunc && typeof customerFunc === 'function') {
                        let validated = customerFunc({
                            required: this.getColumnProps(colKey, 'required'),
                            rowStatus: row.status,
                            attrcode: colKey,
                            values: row.values,
                            column: this.getColumnProps(),
                            rowKeyValue: row.key,
                            saga_status: row.values?.saga_status || {},
                        });

                        invalid = !validated.passed;
                    } else if (reg && !invalidValue) {
                        if (!(typeof reg === 'function' ? reg(value) : reg.test(value))) { //正则校验不通过  
                            invalid = true;
                        } else {
                            invalid = false;
                        }
                    } else {
                        invalid = invalidValue;
                    }

                    invalid && rowErrorLabel.add(colKey);
                    this.setCellProps(row.key, colKey, { invalid });
                });
                
                if (rowErrorLabel.size) {
                    errInfo.set(row.key, rowErrorLabel);
                }
            });

            // 3. 存储已被检查过的数据 TODO 还需要一个get接口
            this.store.validatedInfo = errInfo;

            if (errInfo.size) {
                // 为了更新CardTable页签的错误标识
                this.forceUpdate();
            }
            return errInfo;
        },
        clearValidate: rowKeys => {
            let validatedInfo = this.store.validatedInfo;

            if (!validatedInfo || !validatedInfo.size) { return; } //避免多余的update和循环

            if (rowKeys) { //根据rowKey清除验证
                !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);

                rowKeys.forEach(rowKey => {
                    if (validatedInfo.has(rowKey)) {
                        validatedInfo.get(rowKey).forEach(colKey => {
                            this.setCellProps(rowKey, colKey, { invalid: false });
                        });
                    }
                    validatedInfo.delete(rowKey);
                });
            } else {
                validatedInfo.forEach((colKeys, rowKey) =>
                    colKeys.forEach(colKey => this.setCellProps(rowKey, colKey, { invalid: false })));

                validatedInfo = new Map();
            }

            this.store.validatedInfo = validatedInfo;
            this.forceUpdate();
        },
        clearCellValidate: (rowKey, colKey) => {
            let validatedInfo = this.getValidateInfo();
            if (validatedInfo.has(rowKey)) {
                let colKeys = validatedInfo.get(rowKey);
                colKeys.delete(colKey);
                if (!colKeys.size) {
                    // 为了清除CardTable页签的错误标识
                    validatedInfo.delete(rowKey);
                    this.forceUpdate();
                }
                this.store.validatedInfo = validatedInfo;
            }
        },
        getValidateInfo: () => {
            return this.store.validatedInfo;
        },
        filterEmptyRows: (keys = [], rule = "except", isDeepDel) => {
            let rows = this.getRows(),
                keySet = new Set(keys),
                checkedKeys = this.getCheckedKeys(),
                columns = this.getColumns().filter(col =>
                    isValidType(this.getColumnProps(col.key, 'itemtype')) &&
                    col.key !== 'numberindex' &&
                    (rule === 'except' ? !keySet.has(col.key) : keySet.has(col.key)),
                );

            rows = rows.filter(row => {
                let isEmpty = columns.every(col => ['', null, undefined, false].includes(row.values?.[col.key]?.value));

                if (isEmpty) {
                    //过滤掉要取消已选状态
                    let rowKey = row.key || row.rowId || row.rowid;
                    checkedKeys.delete(rowKey);
                    this.setCheckedKeys(checkedKeys, { shouldUpdate: false });

                    if (!isDeepDel && row.status != ROW_STATUS.ADD) { //非新增态假删   兼容领域status=2的情况 
                        row.status = ROW_STATUS.DELETE;
                        return true;
                    }
                    return false; //真删
                }
                return true;
            });

            return rows;
        },
        setKeyWord: keyWord => {
            this.store.searchKeyWord = keyWord;
        },
        getKeyWord: () => {
            return this.store.searchKeyWord;
        },
    };
};
useStore.propsGenerator = function (props) {
    let { store, autoRowHeight, customSetting } = props,
        { getInstance, getVisibleRows, getVisibleColumns, getAllRowsInfo, getStatus, getConfig, getColumnProps } = store,
        { mergeCell, floatOprCol = 'auto', theme = 'normal' } = getConfig() || {};

    let { itemIDManager } = getInstance(),
        status = getStatus(),
        globalTableConfig = JSON.parse(getGlobalStorage('localStorage', 'nccTable')) || {},
        ncc_automated = getGlobalStorage("localStorage", "_ncc_automated_"),
        hoverContent = true,
        { rowcheckedButton } = { ...globalTableConfig, ...customSetting },
        hasOprColumn = false;

    if (!floatOprCol || floatOprCol === 'auto' && (ncc_automated === '1' || rowcheckedButton !== 'suspension')) {
        hoverContent = false;
    }

    let rows = getVisibleRows(),
        columns = dfs(getVisibleColumns(), column => {
            column = { ...column };
            let {
                attrcode: colKey,
                itemtype,
                visible,
                render,
                color,
                ishighlight,
                required,
                tooltips,
                label,
                customerTitle, // 领域传来的title
                tabskip,
                alignment,
            } = column;

            if (
                hoverContent &&
                (colKey === "opr" || colKey === 'operate') &&
                itemtype === "customer" &&
                render &&
                visible
            ) {
                hasOprColumn = true;
                // 操作列模式切换
                let _title = column.title;
                column.width = 0;
                column.title = <div style={{ display: 'none' }}>{_title}</div>;
                column.resizable = false;
            }

            column.render = row => {
                let rowKey = row.key,
                    itemId = itemIDManager.getID({ rowKey, colKey });
                return (<>
                    {theme === 'simple' && <span>{getColumnProps(colKey).label}:</span>}
                    <Cell
                        key={itemId}
                        rowKey={rowKey}
                        colKey={colKey}
                        store={store}
                        hoverContent={hoverContent}
                        autoRowHeight={autoRowHeight}
                    />
                </>);
            };

            let titleStyle = { color: DEFAULT_COLOR.has(color) ? '' : color };
            if (alignment == '1') {
                titleStyle.direction = 'ltr';
                titleStyle.textAlign = 'left';
            } else if (alignment == '2') {
                titleStyle.direction = 'rtl';
                titleStyle.textAlign = 'right';
            } else if (alignment == '3') {
                titleStyle.justifyContent = 'center';
            }

            column.title = <div
                className={`table-next-header-title header-type-${itemtype}`}
                style={titleStyle}
            >
                <span
                    className={classNames('table-next-header-content', { 'high-light': ishighlight })}
                    title={customerTitle || (typeof label === 'string' && label)}
                >
                    {required && status !== 'browser' && <i className='required-column iconfont icon-xinghao'></i>}
                    {typeof label === 'function' ? label(column) : label}
                </span>
                {tooltips && <Tooltip
                    placement="top"
                    overlay={tooltips}
                    delayShow={300}
                >
                    <i className="iconfont icon-bangzhutishi" />
                </Tooltip>}
            </div>;

            mergeCell && (column.mergeInfo = (row, viewIndex) => {
                return mergeCell({ record: row, index: viewIndex, attrcode: colKey });
            });

            return column;
        }),
        allRowsInfo = getAllRowsInfo();

    let hasSuspendedRow = hoverContent && hasOprColumn;

    return {
        ...props,
        columns,
        rows,
        allRowsInfo,
        className: classNames(props.className, `table-status-${status}`, { 'suspended-row': hasSuspendedRow }),
        locale: store.getLocale(),
        hasSuspendedRow,
        hideHeader: theme === 'simple',
    };
};